import { FC, useCallback, useState } from "react";
import { observer } from "mobx-react";
import { Button, CircularProgress } from "@mui/material";

import { DownloadIcon } from "../../../icons";

import { useClaimsStore } from "../../../../hooks/useClaimsStore";
import useNotifier from "../../../../hooks/useNotifier";
import { apiService } from "../../../../services/apiService";
import { saveBlob } from "../../../../services/fileDownloadService";
import { currentTimeZone } from "../../../../stores/ObservabilityStore";

// Exports the whole Claims queue as the table currently shows it: same filters,
// search, 837/unmatched toggle and sort as loadClaimsData, but every page, and
// only the visible columns in their on-screen order.
const ClaimsExportCsvButton: FC = observer(() => {
  const store = useClaimsStore();
  const notifier = useNotifier();

  const [isDownloading, setIsDownloading] = useState(false);

  const handleExport = useCallback(async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const response = await apiService.downloadClaimsCsv({
        orderBy: store.orderBy,
        filters: store.activeTabFilters,
        keywordSearch: store.claimKeywordSearch,
        missingClaims: store.showMissingClaims,
        columns: store.visibleColumnIds,
        tz: currentTimeZone(),
      });

      if (!response.success || !response.data) {
        notifier.showError(response.error || "Failed to export the claims CSV");
        return;
      }

      saveBlob(response.data.blob, response.data.filename || "claims.csv");
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, store, notifier]);

  return (
    <Button
      variant="text"
      startIcon={
        isDownloading ? (
          <CircularProgress size={18} aria-label="Exporting CSV" />
        ) : (
          <DownloadIcon size={18} />
        )
      }
      onClick={handleExport}
      disabled={isDownloading}
      sx={(theme) => ({
        textTransform: "none",
        fontWeight: 400,
        minWidth: "auto",
        whiteSpace: "nowrap",
        flexShrink: 0,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.paper,
        border: "1px solid",
        borderColor: theme.palette.divider,
        borderRadius: "8px",
        height: "36px",
        px: 1.5,
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
      })}
    >
      Export to CSV
    </Button>
  );
});

export default ClaimsExportCsvButton;
